import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { actionCreators } from '../../store/LoginActionsReducers';
import { withRouter } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import * as pbi from 'powerbi-client';
import api from '../../ApiService';
import { UserRelationshipTypes, UserRoles } from '../../helper';
import TypeAheadSelect from '../TypeAheadSelect';

const powerbi = new pbi.service.Service(pbi.factories.hpmFactory, pbi.factories.wpmpFactory, pbi.factories.routerFactory);

class GroupReports extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            affiliatesAndGpos: [],
            selectedAffiliateOrGpo: { name: undefined, id: undefined, hash: undefined },
            selectedReport: { name: undefined, id: undefined },
            showAffiliateGpoDropdown: false
        };
    }

    componentDidMount = async () => {

        let userRole = this.props.userRole;
        let userRelationshipType = this.props.userRelationshipType;
        let isStaffManagementOrAbove = userRole === UserRoles.Administrator
            || userRole === UserRoles.SuperUser
            || userRole === UserRoles.StaffManagement;
        let isStaffUserOrAbove = isStaffManagementOrAbove
            || userRole === UserRoles.StaffUser
            || userRole === UserRoles.StaffAnalyticsManager;
        let isAffiliate = userRelationshipType === UserRelationshipTypes.AffiliateAreaManager
            || userRelationshipType === UserRelationshipTypes.AffiliateDistrictManager
            || userRelationshipType === UserRelationshipTypes.Affiliate
            || userRole === UserRoles.PathwayStaffAnalyticsManager;
        let isGpo = userRelationshipType === UserRelationshipTypes.GpoAreaManager
            || userRelationshipType === UserRelationshipTypes.GpoDistrictManager
            || userRelationshipType === UserRelationshipTypes.GpoEnterprise;

        let affiliates = [];
        let gpos = [];

        if (isStaffUserOrAbove || isAffiliate)
            affiliates = await api.getLookup('Affiliates');
        if (isStaffManagementOrAbove || isGpo) {
            gpos = await api.getLookup('GPO Options');
        }

        // If user is not iVet admin or above, we will only use their report group hash.
        let selectedAffiliateOrGpo = this.state.selectedAffiliateOrGpo;
        if (affiliates && isAffiliate) {
            selectedAffiliateOrGpo = {
                name: 'Affiliate', // doesn't matter... no dropdown.
                id: `affiliate_${this.props.reportGroupHash}`,
                hash: this.props.reportGroupHash
            };
        }
        else if (gpos && isGpo) {
            selectedAffiliateOrGpo = {
                name: 'Group',
                id: `gpo_${this.props.reportGroupHash}`,
                hash: this.props.reportGroupHash
            };
        }

        let reports = await api.getReports('Group', selectedAffiliateOrGpo.hash);
        if ((affiliates || gpos) && reports) {
            let selectedReport = {
                name: undefined,
                id: undefined
            };

            if (reports.length === 1) {
                selectedReport.name = reports[0].name;
                selectedReport.id = reports[0].id;
            }

            // Until a better UI is worked out, munge affiliates & GPOs into one list.
            let affiliatesAndGpos = [];
            affiliates.forEach((affiliate) => affiliatesAndGpos.push({
                id: `affiliate_${affiliate.id}`,
                name: affiliate.name,
                hash: affiliate.affiliateHash
            }));
            gpos.forEach((gpo) => affiliatesAndGpos.push({
                id: `gpo_${gpo.id}`,
                name: `(GPO) ${gpo.name}`,
                hash: gpo.gpoHash
            }));

            let showAffiliateGpoDropdown = affiliatesAndGpos && (isStaffUserOrAbove || affiliatesAndGpos.length > 1);

            this.setState({
                reports,
                selectedReport,
                affiliatesAndGpos,
                selectedAffiliateOrGpo,
                showAffiliateGpoDropdown,
                loading: false
            }, async () => {
                if (selectedReport.id !== undefined)
                    await this.refreshEmbed(selectedAffiliateOrGpo, selectedReport);
            }
            );
        }
        else {
            this.props.signOut();
        }
    }

    handleReportChange = async (e) => {
        e.preventDefault(); // ?
        let selectedReport = this.state.reports.find((r) => { return r.id === e.target.value; });
        this.setState({ selectedReport });
        await this.refreshEmbed(null, selectedReport);
    };

    handleAffiliateChange = async item => {
        let selectedAffiliateOrGpo = this.state.affiliatesAndGpos.find((a) => { return a.id === item.id; });
        this.setState({ selectedAffiliateOrGpo });
        await this.refreshEmbed(selectedAffiliateOrGpo, null); // (don't get state right after setting it)
    }

    embedReport = async (report) => {
        let accessToken = report.userEmbedToken;
        // Render report
        let reportId = report.id;
        let embedUrl = report.embedUrl;
        let config = {
            type: "report",
            tokenType: pbi.models.TokenType.Embed,
            accessToken: accessToken,
            embedUrl: embedUrl,
            id: reportId,
            permissions: pbi.models.Permissions.All,
            viewMode: pbi.models.ViewMode.View,
            pageView: 'fitToWidth',
            settings: {
                filterPaneEnabled: false,
                navContentPaneEnabled: true
            }
        };
        let embedDiv = document.getElementById('embedDiv');
        if (embedDiv !== null) {
            powerbi.reset(embedDiv);
            let embeddedReport = powerbi.embed(embedDiv, config);
            if (embeddedReport.embeType !== 'report') return; // the misspelling is required
        }
    }

    initEmbeddedReport() {
        let style = { margin: 0, padding: 0, minHeight: "450px", height: "78vh", width: "100%" };
        return (
            <div className={"embedWrapper" + (this.state.loading ? " hidden" : "")}>
                <div id="embedDiv" className="powerbi-container" style={style} ref={(ref) => this.embedContainer = ref} />
            </div>
        );
    }

    refreshEmbed = async (affiliateOrGpo, report) => {
        if (!affiliateOrGpo) affiliateOrGpo = this.state.selectedAffiliateOrGpo;
        if (!report) report = this.state.selectedReport;
        if (!affiliateOrGpo.id || !report.id) return; // not yet!

        let pbiReport = await api.getReport(report.id, affiliateOrGpo.hash, 'Group');
        if (pbiReport) {
            report.userEmbedToken = pbiReport.token;
            report.embedUrl = pbiReport.embedUrl;
            this.embedReport(report);
        }
    }

    toggleFullscreen = (e) => {
        e.preventDefault();
        let report = powerbi.get(this.embedContainer);
        report.fullscreen();
    }

    getReportUrl = () => {
        let location = window.location;
        let port = (location.port === '80' || location.port.length === 0) ? '' : `:${location.port}`;
        let reportUrl = `${location.protocol}//${location.hostname}${port}/#/report/${this.state.selectedReport.id}/${this.state.selectedAffiliateOrGpo.hash}`;

        // !!! Until we add a front end page to support this, redirect to the existing site
        //let reportUrl = `https://ivet360data.azurewebsites.net/#/report/${this.state.selectedReport.id}/${this.state.selectedAffiliateOrGpo.affiliateHash}`;

        let encapsulatedReportUrl = `<iframe src="${reportUrl}" width="1100" height="900" scrolling="no" frameborder="0"></iframe>`;
        return encapsulatedReportUrl;
    }

    renderSelects(affiliatesAndGpos, reports) {
        // Skip if rendering only one report and affiliate dropdown will not be rendered.
        if (!this.state.showAffiliateGpoDropdown && reports && reports.length === 1)
            return null;

        let selectedAffiliateOrGpoDropdown = null;
        if (this.state.selectedAffiliateOrGpo.id) {
            selectedAffiliateOrGpoDropdown = {
                value: this.state.selectedAffiliateOrGpo.hash,
                label: this.state.selectedAffiliateOrGpo.name,
                id: this.state.selectedAffiliateOrGpo.id
            };
        }

        return (
            <Grid container spacing={24}>
                <Grid item xs={6}>
                    {this.state.showAffiliateGpoDropdown &&
                        <div style={{paddingTop: '10px'}}>
                            <TypeAheadSelect
                                value={selectedAffiliateOrGpoDropdown}
                                onChange={this.handleAffiliateChange}
                                placeholder='Affiliate or GPO'
                                options={affiliatesAndGpos.map(a => ({ value: a.hash, label: a.name, id: a.id }))}
                            />
                        </div>
                    }
                </Grid>
                <Grid item xs={6}>
                    {reports && reports.length > 1 &&
                        <FormControl fullWidth >
                            <InputLabel htmlFor="reportId" style={{ fontWeight: 'normal' }}>Report</InputLabel>
                            <Select
                                value={this.state.selectedReport.id || ''}
                                onChange={this.handleReportChange}
                                inputProps={{ name: 'reportId', id: 'reportId' }}
                            >
                                {reports.map(r =>
                                    <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    }
                </Grid>
            </Grid>
        );
    }

    render() {
        let selectLists = this.state.loading
            ? <p style={{ padding: '10px' }}><em>Loading...</em></p>
            : this.renderSelects(this.state.affiliatesAndGpos, this.state.reports);
        let embeddedReport = this.initEmbeddedReport();
        return (
            <React.Fragment>
                <h3>Group Report</h3>
                {selectLists}
                <br />
                {this.state.selectedReport.id && this.state.selectedAffiliateOrGpo.id &&
                    <React.Fragment>
                        <Grid container spacing={24}>
                            <Grid item xs={12}>
                            {this.props.userRole === UserRoles.SuperUser &&
                                <div>
                                    <h4>Embed Code</h4>
                                    <pre><code>{this.getReportUrl()}</code></pre>
                                </div>
                            }
                            </Grid>
                        </Grid>
                        {embeddedReport}
                    </React.Fragment>
                }
            </React.Fragment>);
    }
}

export default withRouter(connect(state => state.login,
    dispatch => bindActionCreators(actionCreators, dispatch)
)(GroupReports));


// WEBPACK FOOTER //
// ./src/components/GroupReports/GroupReports.js